import { useWikiCategory, useWikiQuery, wikiKeys } from '../shared/useWikiQueries';
import { ancestorRulesCategoryId } from './wikiAccessRules';

const enc = encodeURIComponent;

/** `['wiki','category',id,'acl']` — inside the 'category' invalidation scope. */
export function categoryAclKey(id) {
    return [...wikiKeys.category(id), 'acl'];
}

/** `['wiki','page',id,'acl']` — inside the 'page' invalidation scope. */
export function pageAclKey(id) {
    return [...wikiKeys.page(id), 'acl'];
}

/** A scope's access rules (`GET …/acl`, Wiki admins only). */
export function useWikiAcl(scope, id, { enabled = true } = {}) {
    const key = scope === 'page' ? pageAclKey(id) : categoryAclKey(id);
    const base = scope === 'page' ? 'pages' : 'categories';
    const query = useWikiQuery(key, id ? `/wiki/${base}/${enc(id)}/acl` : null, { enabled });
    return { ...query, entries: (query.data && query.data.entries) || [] };
}

/**
 * What a nested scope (subcategory or page) inherits, for the Access editor:
 * the parent category's `access_summary` (per-role levels for "Same as
 * parent") and the rules of the nearest ancestor that has its own (their
 * per-person rows are carried forward, #871).
 */
export function useAccessAncestor(parentId, { enabled = true } = {}) {
    const on = enabled && Boolean(parentId);
    const parent = useWikiCategory(parentId, { enabled: on });
    const ancestorId = on ? ancestorRulesCategoryId(parent.data) : null;
    const rows = useWikiAcl('category', ancestorId, { enabled: on && Boolean(ancestorId) });
    return {
        summary: (parent.data && parent.data.access_summary) || null,
        rows: ancestorId ? rows.entries : [],
        ready: !on || (parent.isSuccess && (!ancestorId || rows.isSuccess)),
        error: (on && (parent.error || (ancestorId && rows.error))) || null,
    };
}

/**
 * `GET /wiki/members` — the people picker's pool and each role's level
 * before any rule (`role_floors`), Wiki admins only.
 */
export function useWikiMembers({ enabled = true } = {}) {
    const query = useWikiQuery(['wiki', 'members'], '/wiki/members', { enabled });
    return {
        ...query,
        members: (query.data && query.data.members) || [],
        roleFloors: (query.data && query.data.role_floors) || null,
    };
}
